import { CdkVirtualScrollViewport, ScrollingModule } from '@angular/cdk/scrolling';
import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { MatCheckboxChange, MatCheckboxModule } from '@angular/material/checkbox';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { UiKitModule } from '@serviceplan/ui-kit';
import {
  AssetPublishingStatus,
  AssetUIModel,
  CanPipe,
  ClientCoreModule,
  ClientEnvironment,
  DefaultAllStatusList,
  ENVIRONMENT,
  FilterAllOptions,
  accessAsset,
  anyAsset,
  getAssetPublishingStatus,
  isAssetPlannedOrUnpublished,
} from '@thehubz/client/core';
import { Asset } from '@thehubz/client/data-access-api';
import { CustomClients } from '@thehubz/shared/branding';
import { AnyType, AssetGridView } from '@thehubz/shared/model';
import { TourMatMenuModule } from 'ngx-ui-tour-md-menu';
import { Subscription, interval, timer } from 'rxjs';
import { filter, map, pairwise, takeUntil, throttleTime } from 'rxjs/operators';
import { AssetPublishingMessagePipe } from '../../pipes/asset-publishing-message.pipe';
import { AssetTypeTranslationPipe } from '../../pipes/asset-type-translation/asset-type-translation.pipe';
import { AssetCommonService } from '../../services/asset-common.service';
import { AssetViewTypeService, GridConfig } from '../../services/asset-view-type.service';
import { AssetProgressBarComponent } from '../asset-progress-bar/asset-progress-bar.component';
import { AssetGalleryCardActionsComponent } from './asset-gallery-card-actions/asset-gallery-card-actions.component';

export interface ScrollPositionState {
  skip: number; // current skip value
  limit: number; // number of items per page
  scrollOffSet: number; // vertical scroll position
  galleryOffSet: number; // number of pages loaded
  assetPositionId: string; // format: rowIndex-index
}

@Component({
  selector: 'thehubz-asset-gallery-view',
  templateUrl: './asset-gallery-view.component.html',
  styleUrls: ['./asset-gallery-view.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    MatIconModule,
    MatTooltipModule,
    MatProgressSpinnerModule,
    FontAwesomeModule,
    UiKitModule,
    MatCheckboxModule,
    ClientCoreModule,
    ScrollingModule,
    AssetPublishingMessagePipe,
    AssetGalleryCardActionsComponent,
    AssetProgressBarComponent,
    TourMatMenuModule,
    AssetTypeTranslationPipe,
  ],
})
export class AssetGalleryViewComponent implements OnDestroy, AfterViewInit, OnChanges {
  @ViewChild('scroller') scroller!: CdkVirtualScrollViewport;
  @ViewChild('progressBarContainer') progressBarContainer!: ElementRef;

  @Input() imageObject: AssetUIModel[] = [];
  @Input() imageSplitObject: AssetUIModel[][] | undefined;
  @Input() filterObject: FilterAllOptions = {
    filters: {},
  };
  @Input() configObject!: AnyType;
  @Input() assetUploadStepperFlag?: boolean;
  @Input() size!: number;
  @Input() itemSize = 310;
  @Input() scrollFlag = true;
  @Input() assetCount!: number;
  @Input() noDownload? = false;
  @Input() dashboardFlag = false;
  @Input() projectFlag = false;
  @Input() relationFlag = false;
  @Input() collectionFlag = false;
  @Input() sharedAssetsFlag = false;
  @Input() AssetType = Asset.AssetTypeEnum;
  @Input() checkedAll = false;
  @Input() offset!: number;
  @Input() emitSelectedAssets = false;
  @Input() collectionOwner = false;
  @Input() enableAssetDetailsLink = false;
  @Input() assetChecked: AssetUIModel[] = [];
  @Input() currentSize!: AnyType;
  @Input() gridSettingsKeySuffix = '';

  @Output() updateSelectedData = new EventEmitter<AssetUIModel[]>();
  @Output() selectedAssets = new EventEmitter<AnyType>();
  @Output() deleteAsset = new EventEmitter<{ asset: AssetUIModel; id: string; planDeletionFlag?: boolean }>();
  @Output() downloadAsset = new EventEmitter<{ asset: AssetUIModel; name: string }>();
  @Output() addRemoveFromBasket = new EventEmitter<AssetUIModel>();
  @Output() selectCollectionAsset = new EventEmitter<{ asset: AssetUIModel; checked: boolean }>();
  @Output() viewportScrolled = new EventEmitter<{ offset: number; direction?: string; anchorIndex?: number }>();
  @Output() editAssets = new EventEmitter<{ editType: string; assets: AssetUIModel[] }>();
  @Output() viewAsset = new EventEmitter<AssetUIModel>();
  @Output() uploadClick = new EventEmitter<AnyType>();

  AnyAsset = anyAsset;
  AccessAsset = accessAsset;
  Status = Asset.StatusEnum;

  selectedData: AssetUIModel[] = [];
  galleryOffset = 0;
  initialOffset = 0;
  bulkEdit = true;

  private scrollerSubscriptionToBottom?: Subscription;
  private scrollerSubscriptionToTop?: Subscription;
  private viewUpdateSubscription?: Subscription;
  private assetSidebarSubscription?: Subscription;

  private gridSettings: GridConfig = {};
  private resizeObserver?: ResizeObserver;

  CustomClients = CustomClients;
  brandingKey!: CustomClients;
  showAssetStatusIcon = false;
  counter = 0;
  itemHeight = 0;

  defaultAllStatusList: AnyType = DefaultAllStatusList;
  lastVisibleAssetIndex = 0;

  isGuidedTourEnabled?: boolean;

  constructor(
    private router: Router,
    private ngZone: NgZone,
    private canPipe: CanPipe,
    private activatedRoute: ActivatedRoute,
    private cdr: ChangeDetectorRef,
    protected assetCommonService: AssetCommonService,
    private assetViewTypeService: AssetViewTypeService,
    @Inject(ENVIRONMENT) env: ClientEnvironment,
  ) {
    this.brandingKey = env.brandingKey ?? CustomClients.Standard;
    this.showAssetStatusIcon = env.assetStatusIcon ?? false;
    this.isGuidedTourEnabled = env.isGuidedTourEnabled;
  }

  ngOnChanges(changes: SimpleChanges) {
    if (this.imageSplitObject && (this.assetChecked.length > 0 || this.sharedAssetsFlag)) {
      this.imageSplitObject.forEach((imageSplit) => {
        imageSplit.forEach((asset) => {
          asset.checked = this.assetChecked.some((item) => item._id === asset._id);
        });
      });
    }

    if (changes['imageSplitObject']) {
      const newInterval = interval(100)
        .pipe(takeUntil(timer(5000)))
        .subscribe(() => {
          const elements = document.querySelectorAll('.asset-gallery-item');
          if (!elements.length) return;
          this.changeProgress();
          newInterval.unsubscribe();
        });
    }

    if (changes['assetCount'] && changes['assetCount'].currentValue !== 0) {
      // the data set has changed, reset the gallery offset - scroll to top
      this.galleryOffset = 0;
      const assetPosition = sessionStorage.getItem('assetGalleryViewScrollPosition');
      if (!assetPosition) {
        this.updateScrollPosition();
      }
    }

    this.gridSettings = this.assetViewTypeService.getGridConfig(this.gridSettingsKeySuffix);

    if (this.scroller) {
      // Wait for the next rendering cycle
      setTimeout(() => {
        this.updateScrollerItemSize();
      });
    }
  }

  ngAfterViewInit(): void {
    this.galleryOffset = this.offset;
    this.initialOffset = this.offset;
    this.registerViewUpdateObserver();
    const assetPosition = sessionStorage.getItem('assetGalleryViewScrollPosition');
    if (assetPosition && assetPosition != null) {
      this.updateScrollPosition(assetPosition);
    }
    if (this.scroller) {
      this.updateScrollerItemSize();

      this.resizeObserver = new ResizeObserver(() => {
        this.updateScrollerItemSize();
      });
      this.resizeObserver.observe(this.scroller.elementRef.nativeElement);

      this.scrollerSubscriptionToBottom = this.scroller
        .elementScrolled()
        .pipe(
          map(() => this.scroller.measureScrollOffset('bottom')),
          pairwise(),
          filter(([y1, y2]) => y2 < y1 && y2 < 100),
          throttleTime(500),
        )
        .subscribe(() => {
          this.ngZone.run(() => {
            this.galleryOffset += 1;
            let newOffset = 0;
            if (this.galleryOffset > this.offset) {
              newOffset = this.galleryOffset;
              const limit = this.filterObject['limit'] ?? 0;
              const skip = limit * newOffset;
              if (skip < this.assetCount) {
                // Normal case: skip points inside the available asset range
                this.filterObject['skip'] = skip;
                if (this.filterObject['skip'] > 0) {
                  this.viewportScrolled.emit({ offset: newOffset });
                }
              } else {
                // When computed skip goes beyond available assets, set skip to the
                // start index of the last page instead of resetting to 0. This
                // prevents jumping back to the start when the user scrolls to the end.
                const lastPageSkip = Math.max(0, Math.floor((Math.max(this.assetCount, 1) - 1) / limit) * limit);
                this.filterObject['skip'] = lastPageSkip;
              }
            }
          });
        });

      this.scrollerSubscriptionToTop = this.scroller
        .elementScrolled()
        .pipe(
          map(() => this.scroller.measureScrollOffset('top')),
          pairwise(),
          filter(([y1, y2]) => y1 > y2 && y2 < 2),
          throttleTime(500),
        )
        .subscribe(() => {
          this.ngZone.run(() => {
            this.initialOffset = this.initialOffset - 1;
            let newOffset = 0;
            if (this.initialOffset > -1 && this.initialOffset < this.offset) {
              newOffset = this.initialOffset;
              const skip = (this.filterObject['limit'] ?? 0) * newOffset;
              this.filterObject['skip'] = this.assetCount > skip ? skip : 0;
              const anchorIndex = this.scroller.getRenderedRange().start;
              this.viewportScrolled.emit({ offset: newOffset, direction: 'top', anchorIndex });
            }
          });
        });

      if (this.brandingKey === CustomClients.Vorwerk) {
        const newInterval = interval(100)
          .pipe(takeUntil(timer(5000)))
          .subscribe(() => {
            const elements = document.querySelectorAll('.asset-gallery-item');
            if (!elements.length) return;
            this.changeProgress();
            newInterval.unsubscribe();
          });
        this.scrollerSubscriptionToBottom = this.scroller.elementScrolled().subscribe(() => {
          this.changeProgress();
        });
      }
    }
  }

  scrollToIndex(index: number, behavior: ScrollBehavior = 'instant') {
    this.scroller.scrollToIndex(index, behavior);
  }

  changeProgress() {
    const element = this.findLastVisibleAsset();
    if (element) {
      this.lastVisibleAssetIndex = this.getAssetIndexFromElement(element);
      this.cdr.detectChanges();
    }
  }

  /**
   * This function is used to restore the scroll position based on selected asset
   * @param assetPosition
   */
  private updateScrollPosition(assetPosition?: string) {
    setTimeout(() => {
      const viewportElement = this.scroller.elementRef.nativeElement as HTMLElement;
      let actualScrollHeight = 0;

      if (assetPosition) {
        const lastAsset = document.getElementById(assetPosition);
        if (lastAsset) {
          const elementRect = lastAsset.getBoundingClientRect();
          actualScrollHeight = elementRect.y - elementRect.height;
        }
      }

      // Scroll the virtual scroller viewport directly
      viewportElement.scrollTo({ top: actualScrollHeight, behavior: 'smooth' });

      sessionStorage.removeItem('assetGalleryViewScrollPosition');
    }, 500);
  }

  private findLastVisibleAsset() {
    const elements = document.querySelectorAll('.asset-gallery-item');
    let lastVisibleElement = null;
    const scrollerBoxBottom = this.progressBarContainer?.nativeElement.getBoundingClientRect().top;
    elements.forEach((element: AnyType) => {
      const elBoxTop = element.getBoundingClientRect().top;
      if (scrollerBoxBottom && scrollerBoxBottom > elBoxTop) {
        lastVisibleElement = element;
      }
    });
    return lastVisibleElement;
  }

  private getAssetIndexFromElement(element: AnyType) {
    if (element) {
      const elementId = element.getAttribute('asset-id');
      const assetIndex = this.imageObject.findIndex((item) => item._id === elementId);
      return assetIndex;
    }
    return 0;
  }

  private registerViewUpdateObserver() {
    this.viewUpdateSubscription = this.assetCommonService.assetPlannedDeletion$.subscribe((asset) => {
      if (this.imageSplitObject) {
        const imageSplitIndex = this.imageSplitObject?.findIndex((product) => {
          return product.some((item) => {
            return item._id === asset._id;
          });
        });
        if (imageSplitIndex !== undefined && imageSplitIndex !== -1) {
          const assetIndex = this.imageSplitObject?.[imageSplitIndex].findIndex((item) => item._id === asset._id);
          if (assetIndex !== undefined && assetIndex !== -1) {
            if (asset.expiryDate) {
              asset['assetExpiryDays'] = this.assetCommonService.getAssetExpireDays(asset.expiryDate);
              this.imageSplitObject[imageSplitIndex][assetIndex] = asset;
            }

            if (asset.plannedDeletion) {
              this.imageSplitObject[imageSplitIndex][assetIndex].plannedDeletion = asset.plannedDeletion;
            }
          }
        }
      }
    });
  }

  onChange(event: MatCheckboxChange, assetObject: AssetUIModel) {
    if (this.configObject.callFrom === 'jackpotAssets') {
      this.selectedAssets.emit(assetObject);
    } else {
      assetObject.checked = event.checked;
      this.assetChecked = this.assetChecked.filter((val) => val._id);
      const idx = this.assetChecked.findIndex((el) => el._id === assetObject._id);
      if (idx != -1) {
        this.assetChecked.splice(idx, 1);
      } else {
        this.assetChecked.push(assetObject);
      }
      this.selectedAssets.emit(this.assetChecked);
    }
  }

  openModal(asset: AssetUIModel, rowIndex: number, columnIndex: number, redirect = true) {
    const indexOffset = rowIndex * this.size;
    if (this.brandingKey === CustomClients.Vorwerk && asset.status === this.Status.Draft) {
      const event = { assets: [asset] };
      this.uploadClick.emit(event);
      return;
    }
    if (asset.inProgress || this.brandingKey === CustomClients.WestLotto) return;
    // add params on opening dialog
    if (this.configObject?.callFrom !== 'createProject') this.assetIDToParam(asset._id);
    if (this.projectFlag) {
      const currentIndex = indexOffset + columnIndex;
      this.updateSelectedObject(this.imageObject[currentIndex]);
    } else {
      this.assetCommonService.updateAssetList(this.imageObject);
      sessionStorage.setItem('assetsGalleryIdsList', JSON.stringify(this.imageObject.map((asset) => asset._id)));
      // To set the asset id in order to scroll to particular asset
      if (!this.configObject || this.configObject?.callFrom === 'collection') {
        const totalAssetAfterSkip = indexOffset - (this.filterObject.skip ?? 0);
        const newRowIndex =
          totalAssetAfterSkip > 0 ? totalAssetAfterSkip / this.size : indexOffset - 48 > 0 ? (indexOffset - 48) / this.size : indexOffset / this.size;
        const assetPosition = newRowIndex + '-' + columnIndex;
        if ((this.filterObject.skip ?? 0) > 0 || (this.filterObject.limit ?? 0) > 48) {
          const assetFutureLimit = (this.filterObject['limit'] ?? 0) + (this.filterObject['skip'] ?? 0);
          if (assetFutureLimit) sessionStorage.setItem('assetFutureLimit', assetFutureLimit.toString());
        }
        sessionStorage.setItem('assetGalleryViewScrollPosition', assetPosition);
      }
      if (redirect) {
        this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
          this.router.navigate(['/assets/detail/' + asset._id]);
        });
      }
    }
  }

  editDraftAssetData(event: AnyType) {
    this.uploadClick.emit(event);
  }
  updateSelectedObject(data: AssetUIModel) {
    this.selectedData.push(data);
    this.updateSelectedData.emit(this.selectedData);
  }

  // method to add asset id to query param
  assetIDToParam(assetID?: string) {
    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: {
        assetId: assetID ? assetID : null,
      },
      // preserve the existing query params in the route
      queryParamsHandling: 'merge',
      // do not trigger navigation
      skipLocationChange: false,
    });
  }

  onDeleteAsset(asset: AssetUIModel, id: string, planDeletionFlag?: boolean) {
    this.deleteAsset.emit({ asset, id, planDeletionFlag });
  }

  onDownloadAsset(asset: AssetUIModel, name: string) {
    this.downloadAsset.emit({ asset, name });
  }

  onAddRemoveFromBasket(asset: AssetUIModel) {
    this.addRemoveFromBasket.emit(asset);
  }

  onSelectCollectionAsset(asset: AssetUIModel, checked: boolean) {
    this.selectCollectionAsset.emit({ asset, checked });
  }

  getGridSettings(name: string) {
    return (this.gridSettings as any)[name];
  }

  isAssetPlannedOrUnpublished(asset: AssetUIModel): boolean {
    return isAssetPlannedOrUnpublished(asset);
  }

  getIsAssetPublished(asset: AssetUIModel): boolean {
    const publishingStatus = getAssetPublishingStatus(asset);
    const statuses = [AssetPublishingStatus.PLANNED, AssetPublishingStatus.UNPUBLISHED];
    if (statuses.includes(publishingStatus)) return false;
    return true;
  }

  checkboxAccessibility(asset: AssetUIModel) {
    // do not allow to select asset if gallery is accessed from relation tab
    if (this.relationFlag) return false;
    return (
      this.canPipe.transform(this.AccessAsset(asset), 'delete') ||
      this.canPipe.transform(this.AccessAsset(asset), 'download') ||
      this.canPipe.transform(this.AccessAsset(asset), 'share') ||
      this.canPipe.transform(this.AccessAsset(asset), 'basket.access') ||
      this.canPipe.transform(this.AccessAsset(asset), 'collection.create')
    );
  }

  updateScrollerItemSize(): void {
    const item = this.scroller.elementRef.nativeElement.querySelector('.asset-gallery-item');

    if (!item) return;

    this.itemHeight = item.clientHeight;
  }

  getAssetClasses(): string {
    if (
      this.dashboardFlag ||
      this.projectFlag ||
      (this.getGridSettings(AssetGridView.GridView.SHOW_GRID_VIEW_SMALL) && !this.getGridSettings(AssetGridView.GridView.SHOW_GRID_VIEW_SIDEBAR))
    ) {
      return 'sm:w-1/2 lg:w-1/4 xl:w-1/4 2xl:w-1/4';
    }

    if (
      !(this.dashboardFlag || this.projectFlag || this.sharedAssetsFlag) &&
      this.getGridSettings(AssetGridView.GridView.SHOW_GRID_VIEW_LARGE) &&
      !this.getGridSettings(AssetGridView.GridView.SHOW_GRID_VIEW_SIDEBAR)
    ) {
      return 'sm:w-1/2 md:w-1/4 lg:w-[calc(100%/8)] xl:w-[calc(100%/8)]  2xl:w-[calc(100%/8)]';
    }

    if (
      !(this.dashboardFlag || this.projectFlag || this.sharedAssetsFlag) &&
      this.getGridSettings(AssetGridView.GridView.SHOW_GRID_VIEW_SIDEBAR) &&
      this.getGridSettings(AssetGridView.GridView.SHOW_GRID_VIEW_SMALL)
    ) {
      return 'md:w-1/3 xl:w-1/3 2xl:w-[calc(100%/3)]';
    }

    if (this.sharedAssetsFlag) {
      return 'sm:w-1/2 lg:w-1/4';
    }

    if (
      !(this.dashboardFlag || this.projectFlag || this.sharedAssetsFlag) &&
      this.getGridSettings(AssetGridView.GridView.SHOW_GRID_VIEW_SIDEBAR) &&
      this.getGridSettings(AssetGridView.GridView.SHOW_GRID_VIEW_LARGE)
    ) {
      return 'md:w-1/3 xl:w-[calc(100%/6)] 2xl:w-[calc(100%/6)]';
    }

    return '';
  }

  ngOnDestroy(): void {
    this.scrollerSubscriptionToBottom?.unsubscribe();
    this.scrollerSubscriptionToTop?.unsubscribe();
    this.viewUpdateSubscription?.unsubscribe();
    this.assetSidebarSubscription?.unsubscribe();
    this.resizeObserver?.disconnect();
  }
}
